import { apiClient } from './api/client';
import {
  getMembers,
  getAllMembers,
  getMemberById,
  searchMembers,
  searchAllMembers,
  createMember,
  updateMember,
  deleteMember,
  notifyMember,
  updateMemberRole,
  updateMemberStatus,
  resetMemberPassword,
} from './members';

const ENV_SOURCE = (typeof import.meta !== 'undefined' && import.meta.env)
  ? import.meta.env
  : (typeof process !== 'undefined' ? process.env : {});

const USE_MOCK_DATA = String(ENV_SOURCE?.VITE_ENABLE_MOCK_DATA ?? 'false').toLowerCase() === 'true';
const API_BASE_URL = String(ENV_SOURCE?.VITE_API_URL ?? '').replace(/\/+$/, '');

let mockDataPromise;
let eventsModulePromise;

const loadMockData = async () => {
  if (!mockDataPromise) {
    mockDataPromise = import('./mocks/mockData.js');
  }
  return mockDataPromise;
};

const loadEventsModule = async () => {
  if (!eventsModulePromise) {
    eventsModulePromise = import('./events');
  }
  return eventsModulePromise;
};

function normalizeStorageUrl(url) {
  if (!url || typeof url !== 'string') return url;
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith('/') && API_BASE_URL) {
    return `${API_BASE_URL}${url}`;
  }
  return url;
}

function normalizeText(value) {
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

function buildSettingsPayload(settings = {}) {
  // Backend contract: notificationPreference is an object like { email: boolean, sms: boolean }.
  // UI uses a string enum: email | text | both.
  const prefRaw = settings.notificationPreference;
  const notificationPreference =
    typeof prefRaw === 'string'
      ? {
          email: prefRaw === 'email' || prefRaw === 'both',
          sms: prefRaw === 'text' || prefRaw === 'both',
        }
      : prefRaw;

  return {
    businessAddress: normalizeText(settings.businessAddress),
    linkedin: normalizeText(settings.linkedin),
    twitter: normalizeText(settings.twitter),
    facebook: normalizeText(settings.facebook),
    googleBusinessProfileUrl: normalizeText(settings.googleBusinessProfileUrl),
    industry: normalizeText(settings.industry),
    services: normalizeText(settings.services),
    sixMonthGoals: normalizeText(settings.sixMonthGoals),
    idealReferral: normalizeText(settings.idealReferral),
    privacySettings: settings.privacySettings,
    notificationPreference,
  };
}

async function getUserSettingsReal(userId) {
  if (userId === undefined || userId === null) {
    throw new Error('userId is required to load settings');
  }
  return apiClient.get(`/api/settings/${userId}`);
}

async function updateUserSettingsReal(userId, settings) {
  if (userId === undefined || userId === null) {
    throw new Error('userId is required to update settings');
  }
  const payload = buildSettingsPayload(settings);
  return apiClient.put(`/api/settings/${userId}`, payload);
}

async function generateAIPromptReal(payload) {
  if (!payload?.businessName || !payload?.industry || !payload?.services) {
    throw new Error('businessName, industry, and services are required');
  }
  return apiClient.post('/api/settings/ai-prompt', payload);
}

async function uploadProfilePhotoReal(file, memberId) {
  if (!file) {
    throw new Error('Profile photo file is required');
  }
  const formData = new FormData();
  formData.append('file', file);
  if (memberId) {
    formData.append('member_id', String(memberId));
  }
  const response = await apiClient.post('/api/settings/upload/profile-photo', formData);
  if (!response?.url) {
    throw new Error('Upload response missing URL');
  }
  return { ...response, url: normalizeStorageUrl(response.url) };
}

async function uploadBusinessLogoReal(file, memberId) {
  if (!file) {
    throw new Error('Business logo file is required');
  }
  const formData = new FormData();
  formData.append('file', file);
  if (memberId) {
    formData.append('member_id', String(memberId));
  }
  const response = await apiClient.post('/api/settings/upload/business-logo', formData);
  if (!response?.url) {
    throw new Error('Upload response missing URL');
  }
  return { ...response, url: normalizeStorageUrl(response.url) };
}

// Re-export data functions with mock implementations (will be replaced with NBNA_BE)
export const getUserSettings = async (userId) => {
  if (USE_MOCK_DATA) {
    const mockData = await loadMockData();
    return mockData.getUserSettings(userId);
  }
  return getUserSettingsReal(userId);
};

export async function updateUserSettings(userId, settings) {
  if (USE_MOCK_DATA) {
    const mockData = await loadMockData();
    return mockData.updateUserSettings(settings ?? userId);
  }
  return updateUserSettingsReal(userId, settings);
}

export async function generateAIPrompt(payload) {
  if (USE_MOCK_DATA) {
    const mockData = await loadMockData();
    return mockData.generateAIPrompt(payload);
  }
  return generateAIPromptReal(payload);
}

export async function uploadProfilePhoto(file, memberId) {
  if (USE_MOCK_DATA) {
    const mockData = await loadMockData();
    return mockData.uploadProfilePhoto(file, memberId);
  }
  return uploadProfilePhotoReal(file, memberId);
}

export async function uploadBusinessLogo(file, memberId) {
  if (USE_MOCK_DATA) {
    const mockData = await loadMockData();
    return mockData.uploadBusinessLogo(file, memberId);
  }
  return uploadBusinessLogoReal(file, memberId);
}

export async function getMyMember() {
  return apiClient.get('/api/members/me');
}

export async function updateMyMember(payload) {
  return apiClient.patch('/api/members/me', payload);
}

export const getEvents = async (...args) => {
  const eventsModule = await loadEventsModule();
  return eventsModule.getEvents(...args);
};

export const getEventById = async (...args) => {
  const eventsModule = await loadEventsModule();
  return eventsModule.getEventById(...args);
};

export const searchEvents = async (...args) => {
  const eventsModule = await loadEventsModule();
  return eventsModule.searchEvents(...args);
};

export const createEvent = async (...args) => {
  const eventsModule = await loadEventsModule();
  return eventsModule.createEvent(...args);
};

export const updateEvent = async (...args) => {
  const eventsModule = await loadEventsModule();
  return eventsModule.updateEvent(...args);
};

export const deleteEvent = async (...args) => {
  const eventsModule = await loadEventsModule();
  return eventsModule.deleteEvent(...args);
};

export const attendEvent = async (...args) => {
  const eventsModule = await loadEventsModule();
  return eventsModule.attendEvent(...args);
};

export const unattendEvent = async (...args) => {
  const eventsModule = await loadEventsModule();
  return eventsModule.unattendEvent(...args);
};

export const addEvent = async (...args) => {
  const eventsModule = await loadEventsModule();
  return eventsModule.addEvent(...args);
};

export const toggleAttendance = async (...args) => {
  const eventsModule = await loadEventsModule();
  return eventsModule.toggleAttendance(...args);
};

// Member operations
export {
  getMembers,
  getAllMembers,
  getMemberById,
  searchMembers,
  searchAllMembers,
  createMember,
  updateMember,
  deleteMember,
  notifyMember,
  updateMemberRole,
  updateMemberStatus,
  resetMemberPassword,
};

// Prospect operations
export const submitProspectApplication = async (...args) => {
  if (USE_MOCK_DATA) {
    const mockData = await loadMockData();
    return mockData.submitProspectApplication(...args);
  }
  // Live path
  const payload = args?.[0];
  return apiClient.post('/api/prospects/apply', payload);
};

export const getAdminMembers = async (...args) => {
  const adminModule = await import('./admin');
  return adminModule.getAdminMembers(...args);
};
